import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSearchResults, setSearchCount } from "../../../store/search/searchSlice";
import { Breadcrumb } from "../../../components/common/u1/Breadcrumb";
import useSearchCompany from "../../../Hooks/useSearchCompany";
import { OwnerCard } from "../../../components/common/static/u1/OwnerCard";
import { ResearcherCard } from "../../../components/common/static/u1/ResearcherCard";
import { SearchBar } from "../../../components/public/Search/u1/SearchBar";
import { SearchResults } from "../../../components/public/Search/u1/SearchResults";
import { FilterChips } from "../../../components/common/u1/FilterChips";
import { filterCompanies } from "../../../components/common/u1/filters";
import { StateLegend } from "../../../components/common/static/u1/StateLegend";
import { ResultSkeleton } from "../../../components/common/u1/ResultSkeleton";
import { cn } from "../../../components/common/u1/cn";
import { WIDE_MEASURE } from "../../../components/common/u1/measure";
import { SearchEmptyState, SearchNotice } from "../../../components/public/Search/u1/SearchStates";
import "../../../components/common/u1/tokens.css";

/* Results per page requested from the API. SearchNewAPI accepts `page_size`
   (DRF PageNumberPagination; default 10, max 100) — this is the one place the
   page length is set, and it is sent to the server rather than applied here. */
const PAGE_SIZE = 10;

/* u1 Search page shell. An isolated rebuild of the /search layout served at /u1/search. The active /search route is untouched — this page reads the same Redux... */
export default function SearchPage() {
  const dispatch = useDispatch();
  const { searchQuery, searchResults, worldBoxToken, countryData } = useSelector(
    (state) => state.search
  );
  const { token, isLoggedIn } = useSelector((state) => state.auth);

  /* Transcribed from BusinesProfileWidget: a signed-in owner goes straight to
     the listing flow; a signed-out one is sent to log in first, carrying the
     step so Login can replay it afterwards. */
  const listBusinessTo = isLoggedIn ? "/sign-up" : "/login";
  const listBusinessState = { redirectToStep: 6 };
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");

  /* Choosing a filter rewrites the list under the reader, so the page is taken back to the TOP. */
  const chooseFilter = (next) => {
    setFilter(next);
    /* Guarded: jsdom has no scrollTo, and older browsers none that take
       options. A page that does not scroll is a far smaller problem than a
       filter that throws. */
    if (typeof window.scrollTo === "function") {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        /* Pre-options implementations take two numbers. */
        window.scrollTo(0, 0);
      }
    }
  };

  /* Reset paging when the query changes — during render, not in an effect. */
  const [pagedQuery, setPagedQuery] = useState(searchQuery);
  if (pagedQuery !== searchQuery) {
    setPagedQuery(searchQuery);
    setPage(1);
    setFilter("all");
  }

  const { data, isLoading, error, message, meta } = useSearchCompany(
    searchQuery,
    countryData?.code,
    token,
    worldBoxToken,
    true,
    false,
    page,
    PAGE_SIZE
  );

  useEffect(() => {
    localStorage.removeItem("fromFooter");
    if (data) {
      // `data` is one server page; the count is the directory-wide total.
      dispatch(setSearchResults(data));
      dispatch(setSearchCount(meta.totalResults));
    }
  }, [data, meta.totalResults, dispatch]);

  /* No manual refetch on page change: `page` is a dependency of the hook's own
     effect, so changing it refetches once. Calling refetch() here as well would
     issue a second, identical request for every page turn. */

  /* Restyles the global RootLayout header for this page only — cream surface, pinned to the top — while leaving its markup and session-aware buttons exactly as... */
  useEffect(() => {
    document.body.classList.add("u1-page");
    return () => document.body.classList.remove("u1-page");
  }, []);

  const visibleResults = useMemo(
    () => filterCompanies(searchResults, filter),
    [searchResults, filter]
  );

  /* Server pagination when the API provides it, client pagination when it does
     not. SearchNewAPI only started paging in TIP-603's backend commit, which is
     not on development yet — without this the page renders every row returned
     with no pager at all. When the paged endpoint ships, `serverPaged` flips
     and the slicing below stops being used, with no further change here.
     A client-side status filter also has to be paged here rather than by the
     server, since the API has no status parameter. */
  const clientPaged = !meta.serverPaged || filter !== "all";

  const totalPages = clientPaged
    ? Math.max(1, Math.ceil(visibleResults.length / PAGE_SIZE))
    : meta.totalPages;

  const pageRows = clientPaged
    ? visibleResults.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : visibleResults;

  /* A shrinking result set must not strand the user past the last page. */
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // TIP-556 — both "order a web profile" links carry the failed search so the
  // form arrives pre-filled (same state shape as the NotFoundCompany card).
  const orderWebProfileState = {
    prefill: { companyName: searchQuery, countryCode: countryData?.code },
  };

  /* The count is a directory-wide total whenever the number in hand covers the whole result set — unfiltered, or filtered over a list the client holds in full. */
  const countIsPageOnly = filter !== "all" && meta.serverPaged;
  const resultCount = filter === "all" ? meta.totalResults : visibleResults.length;

  const hasSearched = Boolean(searchQuery);
  const noResultsAtAll = searchResults?.length === 0 && !isLoading;
  const filteredToNothing = searchResults?.length > 0 && visibleResults.length === 0;
  const tooManyHits = error?.includes("WorldboxSearch returned too many hits");

  /* The page's own `px-4 sm:px-6 lg:px-8` is gone: WIDE_MEASURE carries its
     padding, and both layers together would inset the results a further 32px
     past the bar at lg. */
  return (
    <div className="u1-root bg-[var(--bg)] min-h-screen w-full">
      <div className={cn(WIDE_MEASURE, "pt-5")}>
        <Breadcrumb />
      </div>

      <div
        className={cn(
          WIDE_MEASURE,
          "pb-20 mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-7"
        )}
      >
        {/* ── Results column ── */}
        <div className="min-w-0">
          {/* Pinned search band, directly beneath the pinned site header. */}
          <div className="sticky top-20 z-10 bg-[var(--bg)] pt-3 pb-1 -mt-2.5">
            <SearchBar isLoading={isLoading} resultsPath="/search" />
          

          {message !== "" && (
            <SearchNotice
              tone={message === "error" ? "error" : "warning"}
              orderWebProfileState={orderWebProfileState}
              showActions={message === "error"}
            >
              {message === "error" ? "Technical issues may affect results." : message}
            </SearchNotice>
          )}

          {/* Count + filters — nothing to count or filter before a search. */}
          {hasSearched && !isLoading && (
            <div className="flex items-center justify-between gap-3 flex-wrap mt-5 mb-3">
              {/* Unfiltered, the count is the server's total across every page. */}
              <p className="font-bold text-[var(--ink)] font-sans m-0">
                {resultCount} {resultCount === 1 ? "business" : "businesses"}{" "}
                {countIsPageOnly ? "on this page" : "found"}
              </p>
              {/* `w-fit mx-auto` centres the row on phones while it fits and
                  lets it scroll from the left if it ever does not — unlike
                  `w-full justify-center`, which would hide the first chip. */}
              <FilterChips
                value={filter}
                onChange={chooseFilter}
                className="w-fit mx-auto sm:mx-0"
              />
            </div>
          )}
          </div>

          {isLoading && (
            <div className="mt-4">
              <ResultSkeleton rows={3} />
            </div>
          )}

          {noResultsAtAll && (
            <div className="mt-4">
              <SearchEmptyState
                heading={
                  tooManyHits ? "Too many results. Please refine your business search." : undefined
                }
                query={searchQuery}
                orderWebProfileState={orderWebProfileState}
                listBusinessTo={listBusinessTo}
                listBusinessState={listBusinessState}
              />
            </div>
          )}

          {filteredToNothing && !isLoading && (
            <div className="mt-4">
              <SearchEmptyState
                heading="No businesses match this filter."
                query={searchQuery}
                orderWebProfileState={orderWebProfileState}
                listBusinessTo={listBusinessTo}
                listBusinessState={listBusinessState}
              />
            </div>
          )}

          {visibleResults.length > 0 && !isLoading && (
            <SearchResults
              data={pageRows}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
            />
          )}
        </div>

        {/* ── Sidebar ──
            Rail order follows the design export: what the badges mean, then
            the owner path, then the researcher path. TIP-556's prefill payload
            is forwarded to both order links so a failed search still arrives
            at the form populated. */}
        <aside className="grid gap-4 content-start min-w-0">
          <StateLegend />
          <OwnerCard ctaTo={listBusinessTo} ctaState={listBusinessState} />
          <ResearcherCard orderWebProfileState={orderWebProfileState} />
        </aside>
      </div>
    </div>
  );
}
